import {
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  SaveOutlined,
} from '@ant-design/icons';
import {
  Drawer,
  Button,
  message,
  Spin,
  Row,
  Col,
  Typography,
  ModalProps,
  Modal,
  notification,
} from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import Form from '../../../components/Form';
import MindsightKeyPairForm, {
  MindsightKeyPairFormValues,
} from '../../../components/MindsightKeyPairForm';
import PageTitle from '../../../components/PageTitle';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../../hooks/useTypedNavigate';
import useTypedParams from '../../../hooks/useTypedParams';
import { withRouteChange } from '../../../services/APM';
import styles from './EditMindsightKeyPair.module.css';

interface ConfirmDeleteModalProps extends ModalProps {
  onSuccess?: () => void | Promise<unknown>;
}

const ConfirmDeleteModal = (props: ConfirmDeleteModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      okButtonProps={{
        htmlType: 'button',
      }}
      okText={t('Sim, deletar chave')}
      title={
        <Typography.Text strong>
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} />
          <Trans>Atenção!</Trans>
        </Typography.Text>
      }
      {...props}
    >
      <Typography.Text>
        <Trans>
          Ao confirmar, a chave será excluído permanentemente e não poderá ser recuperado. Tem
          certeza que deseja continuar?
        </Trans>
      </Typography.Text>
    </Modal>
  );
};

const EditMindsightKeyPair = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<MindsightKeyPairFormValues>();
  const { tenant, key_pair_id } = useTypedParams({ tenant: String, key_pair_id: Number });
  const queryClient = useQueryClient();

  const [confirmDeleteIsOpen, setConfirmDeleteIsOpen] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const keyPair = useAPIQuery(`/mindsight/key-pairs/${key_pair_id}/`);

  const navigate = useTypedNavigate();

  const [setIsDirty] = useDiscardChangesModal(form);

  const mutation = useAPIMutation(
    (variables: MindsightKeyPairFormValues, api) => {
      return api.patch(`/mindsight/key-pairs/${key_pair_id}/`, {
        client_id: variables.clientId,
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Chave atualizada!'));
        navigate({
          pathname: '/:tenant/settings/cs-staff/mindmatch-integration/',
          params: { tenant },
        });
      },
      onError: (err, variables, context) => {
        if (err.response?.data?.client_id?.[0] === 'Invalid Client ID') {
          return notification.error({
            duration: 0,
            message: t('Erro'),
            description: t('Erro na configuração da integração com Mindmatch: Client ID inválido.'),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/mindsight/key-pairs/');
        queryClient.invalidateQueries(`/mindsight/key-pairs/${key_pair_id}/`);
      },
    }
  );

  const onDeleteMutation = useAPIMutation(
    (_, api) => api.delete(`/mindsight/key-pairs/${key_pair_id}/`),
    {
      onSuccess: () => {
        message.success(t('Chave excluída com sucesso!'));
        navigate({
          pathname: '/:tenant/settings/cs-staff/mindmatch-integration/',
          params: { tenant },
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries('/mindsight/key-pairs/');
        queryClient.invalidateQueries(`/mindsight/key-pairs/${key_pair_id}/`);
      },
    }
  );

  return (
    <>
      <ConfirmDeleteModal
        key={`ConfirmSoftDeleteModal-${key_pair_id}`}
        open={confirmDeleteIsOpen}
        onOk={() => {
          setConfirmDeleteIsOpen(false);
          onDeleteMutation.mutate({});
        }}
        onCancel={() => setConfirmDeleteIsOpen(false)}
        onSuccess={() => {
          setConfirmDeleteIsOpen(false);
        }}
        destroyOnClose
      />
      <Drawer
        className={styles.drawer}
        title={
          <PageTitle
            title={`Chave ${keyPair.data?.id || '...'}`}
            level={2}
            extra={[
              <Button
                key="close"
                type="text"
                onClick={() =>
                  navigate({
                    pathname: '/:tenant/settings/cs-staff/mindmatch-integration/',
                    params: { tenant },
                  })
                }
                shape="circle"
              >
                <CloseOutlined />
              </Button>,
            ]}
          />
        }
        maskClosable
        size="large"
        closable={false}
        width={720}
        footer={
          <Row gutter={[16, 16]}>
            <Col>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={mutation.isLoading || onDeleteMutation.isLoading}
                onClick={() => form?.submit()}
              >
                {' '}
                <Trans>Salvar</Trans>
              </Button>
            </Col>
            <Col>
              <Button
                loading={mutation.isLoading || onDeleteMutation.isLoading}
                onClick={() =>
                  navigate({
                    pathname: '/:tenant/settings/cs-staff/mindmatch-integration/',
                    params: { tenant },
                    search: searchParams,
                  })
                }
              >
                <Trans>Fechar</Trans>
              </Button>
            </Col>
            <Col flex={1} />
            <Col>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={mutation.isLoading || onDeleteMutation.isLoading}
                onClick={() => setConfirmDeleteIsOpen(true)}
              >
                {' '}
                <Trans>Deletar</Trans>
              </Button>
            </Col>
          </Row>
        }
        open
        onClose={() =>
          navigate({
            pathname: '/:tenant/settings/cs-staff/mindmatch-integration/',
            params: { tenant },
          })
        }
      >
        <Helmet>
          <title>
            {t('Editar | Chave')}
            {keyPair.data?.id.toString() || '...'} | {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <ErrorResult error={keyPair.error}>
          <Spin spinning={keyPair.isFetching}>
            <MindsightKeyPairForm
              form={form}
              initialValues={{
                clientId: keyPair.data?.client_id || '',
                publicKey: keyPair.data?.public_key || '',
              }}
              layout="vertical"
              onValuesChange={() => setIsDirty(form.isFieldsTouched())}
              onFinish={(variables) => {
                mutation.mutate(variables);
              }}
            />
          </Spin>
        </ErrorResult>
      </Drawer>
    </>
  );
};

export default withRouteChange()(EditMindsightKeyPair);
